import React, { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';
import IconArrowUp from '@/assets/icons/icon-arrow-up-03.svg?react';
import IconArrowDown from '@/assets/icons/icon-arrow-down-03.svg?react';

// Number input with up/down arrows and suffix
interface NumberFieldProps {
  value: number; // current value (ALWAYS comes from parent)
  onChange: (value: number) => void; // callback when value changes.
  onChangeComplete?: (
    event: React.KeyboardEvent<HTMLInputElement> | React.FocusEvent<HTMLInputElement, Element>,
  ) => void; // callback when value changes
  /** Called once when an arrow drag actually starts (first mousemove after mousedown). */
  onDragStart?: (value: number) => void;
  /** Called on every mousemove during an arrow drag (live updates). */
  onDrag?: (value: number) => void;
  /** Called once on mouseup after a drag has happened. */
  onDragEnd?: (value: number) => void;
  text?: string; // label text
  min: number; // min value
  max: number; // max value
  step?: number; // step size
  suffix?: string; // text after number (like px)
  allowDecimal?: boolean; // allow decimals
  className?: string; // extra class for wrapper
  classNameInput?: string; // extra class for input
  formatDisplayValue?: (val: number | string) => string; // custom display
  getNumericInput?: (val: string) => string; // custom parse
  timeMode?: boolean; // Enable time input mode (MM:SS)
  arrows?: boolean; // Show up/down arrows
  preLetter?: string; // Show letter before number
  disabled?: boolean;
  dataTestId?: string;
  id?: string; // Optional ID to override the auto-generated ID (for label association)
  'data-qa-selector'?: string;
}

export const NumberField: React.FC<NumberFieldProps> = ({
  value,
  onChange,
  text = '',
  min,
  max,
  step = 1,
  suffix,
  allowDecimal = false,
  className,
  classNameInput,
  formatDisplayValue,
  getNumericInput,
  timeMode = false, // Default to false
  arrows = true, // Show arrows by default
  preLetter = undefined,
  disabled,
  onChangeComplete,
  onDragStart,
  onDrag,
  onDragEnd,
  dataTestId,
  id,
  'data-qa-selector': dataQaSelector,
}) => {
  // Generate unique ID for label/input association, or use provided ID
  const generatedId = useId();
  const inputId = id || generatedId;

  // Format value for time mode
  const formatTime = (val: number) => {
    const minutes = Math.floor(val / 60)
      .toString()
      .padStart(2, '0');
    const seconds = (val % 60).toString().padStart(2, '0');
    return `${minutes}:${seconds}`;
  };

  // Parse time input back to seconds
  const parseTime = (val: string) => {
    const [minutes, seconds] = val.split(':').map(Number);
    if (isNaN(minutes) || isNaN(seconds)) return 0;
    return minutes * 60 + seconds;
  };

  // Determine the format function
  const format = useCallback(
    (val: number | string) => {
      if (timeMode) {
        if (typeof val === 'number') {
          formatTime(val);
        }
        return String(val);
      }
      if (formatDisplayValue) {
        return formatDisplayValue(val);
      }
      return String(val) + (suffix || '');
    },
    [timeMode, formatDisplayValue, suffix],
  );

  // Determine the parse function
  const getNum = useCallback(
    (val: string) => {
      if (timeMode) {
        return parseTime(val).toString();
      }
      if (getNumericInput) {
        return getNumericInput(val);
      }
      if (suffix && val.endsWith(suffix)) {
        return val.slice(0, -suffix.length);
      }
      return val;
    },
    [timeMode, getNumericInput, suffix],
  );

  const [isFocused, setIsFocused] = useState(false);
  const [inputValue, setInputValue] = useState<string>(format(value));
  const actualStep = step;

  // Drag state
  const [isDragging, setIsDragging] = useState(false);
  const [dragStartY, setDragStartY] = useState(0);
  const [dragStartValue, setDragStartValue] = useState(0);
  const [dragDirection, setDragDirection] = useState<'up' | 'down'>('up');
  const dragStartedRef = useRef(false);

  const formattedValue = useMemo(() => format(value), [format, value]);

  // Always sync inputValue with value from parent when not focused
  useEffect(() => {
    if (!isFocused && !isDragging && inputValue !== formattedValue) {
      setInputValue(formattedValue);
    }
  }, [formattedValue, isFocused, isDragging, inputValue]);

  // Handle mouse move during drag
  const handleMouseMove = useCallback(
    (e: MouseEvent) => {
      if (!isDragging) return;
      document.documentElement.style.cursor = 'ns-resize';
      const deltaY = dragStartY - e.clientY; // positive deltaY = moving up, negative = moving down
      const sensitivity = 2; // pixels per step
      const steps = Math.floor(Math.abs(deltaY) / sensitivity);

      let newValue = dragStartValue;

      // Determine direction based on actual mouse movement
      if (deltaY > 0) {
        // Mouse moved up -> increase value
        newValue = dragStartValue + steps * actualStep;
      } else if (deltaY < 0) {
        // Mouse moved down -> decrease value
        newValue = dragStartValue - steps * actualStep;
      }

      // Clamp to min/max
      newValue = Math.max(min, Math.min(max, newValue));

      if (!dragStartedRef.current) {
        dragStartedRef.current = true;
        onDragStart?.(newValue);
      }
      onDrag?.(newValue);

      if (newValue !== value) {
        onChange(newValue);
      }
    },
    [
      isDragging,
      dragStartY,
      dragStartValue,
      actualStep,
      min,
      max,
      value,
      onChange,
      onDragStart,
      onDrag,
    ],
  );

  const handleMouseUp = useCallback(
    (e: MouseEvent) => {
      document.body.classList.remove('user-select-none');
      document.body.classList.remove('pointer-events-none');
      document.documentElement.style.cursor = 'default';

      const deltaY = dragStartY - e.clientY; // positive deltaY = moving up, negative = moving down
      const sensitivity = 2; // pixels per step
      const steps = Math.floor(Math.abs(deltaY) / sensitivity);

      let newValue = dragStartValue;

      // Determine direction based on actual mouse movement
      if (deltaY > 0) {
        // Mouse moved up -> increase value
        newValue = dragStartValue + steps * actualStep;
      } else if (deltaY < 0) {
        // Mouse moved down -> decrease value
        newValue = dragStartValue - steps * actualStep;
      }

      // Clamp to min/max
      newValue = Math.max(min, Math.min(max, newValue));

      // Trigger onChangeComplete when drag ends
      if (isDragging && onChangeComplete) {
        // Create a synthetic event-like object for consistency
        const syntheticEvent = {
          type: 'mouseup',
          target: { value: newValue.toString() },
          currentTarget: { value: newValue.toString() },
        } as React.FocusEvent<HTMLInputElement, Element>;

        onChangeComplete(syntheticEvent);
      }

      if (dragStartedRef.current) {
        onDragEnd?.(newValue);
        dragStartedRef.current = false;
      }

      setIsDragging(false);
      setDragStartY(0);
      setDragStartValue(0);
    },
    [isDragging, onChangeComplete, onDragEnd, value],
  );

  // Add/remove global mouse listeners
  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    } else {
      document.documentElement.style.cursor = 'default';
    }
  }, [isDragging, handleMouseMove, handleMouseUp, value]);

  // Handle input change for time mode
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement> & { key?: string }) => {
    let val = e.target.value;
    if (timeMode) {
      const regex = /^\d{0,2}:?\d{0,2}$/; // Allow MM:SS format
      if (!regex.test(val)) return;
      setInputValue(val);
      const seconds = parseTime(val);
      if (!isNaN(seconds)) {
        onChange(seconds);
      }
    } else {
      val = getNum(val);
      const regex = allowDecimal ? /^\d*\.?\d*$/ : /^\d*$/;
      if (regex.test(val)) {
        setInputValue(val);
        if (val !== '') {
          const num = Number(val);
          if (!isNaN(num)) {
            const clampedNum = Math.max(min, Math.min(max, num));
            onChange(clampedNum);
          }
        }
      }
    }
  };

  // On blur, show formatted value and sync with parent value
  const handleBlur = () => {
    setIsFocused(false);
    setInputValue(format(value));
  };

  // On focus, show raw value or formatted time for timeMode
  const handleFocus = () => {
    setIsFocused(true);
    if (timeMode) {
      setInputValue(formatTime(value)); // Show formatted MM:SS value
    } else {
      setInputValue(getNum(inputValue));
    }
  };

  const handleArrowMouseDown = (e: React.MouseEvent, direction: 'up' | 'down') => {
    e.preventDefault();
    e.stopPropagation();

    // Use the current value from props as the base for increment/decrement
    const currentValue = value;

    // Calculate the new value based on direction
    const nextValue =
      direction === 'up'
        ? Math.min(currentValue + actualStep, max)
        : Math.max(currentValue - actualStep, min);

    // Update the input display immediately
    if (isFocused) {
      if (timeMode) {
        setInputValue(formatTime(nextValue));
      } else {
        setInputValue(nextValue.toString());
      }
    }

    // Trigger the onChange callback
    onChange(nextValue);

    // Set up drag state for potential dragging
    setIsDragging(true);
    document.body.classList.add('user-select-none');
    document.body.classList.add('pointer-events-none');
    setDragStartY(e.clientY);
    setDragStartValue(nextValue); // Use the new value as drag start
    setDragDirection(direction);
  };

  return (
    <div className={cn('flex items-center justify-between gap-2', className)}>
      {text && (
        <label
          htmlFor={inputId}
          className="text-button-sm justify-start font-medium text-neutral-500"
        >
          {text}
        </label>
      )}
      <div
        className={cn('relative inline-flex items-center')}
        onBlur={(e) => {
          // If focus leaves the whole input+arrows
          if (!e.currentTarget.contains(e.relatedTarget as Node)) handleBlur();
        }}
      >
        {preLetter && (
          <span className="text-button-sm absolute left-2 flex items-center leading-normal font-medium text-neutral-500">
            {preLetter}
          </span>
        )}
        <Input
          id={inputId}
          {...(dataTestId && { 'data-testid': dataTestId })}
          data-qa-selector={dataQaSelector}
          className={cn(
            'h-8 min-h-8 pr-5 pl-1 text-center',
            timeMode ? 'w-[80px]' : suffix ? 'w-[68px]' : 'w-[52px]',
            classNameInput,
          )}
          type="text"
          min={min}
          max={max}
          step={step}
          value={isFocused ? inputValue : formattedValue} // always show parent value when not focused
          onChange={handleInputChange}
          onChangeComplete={onChangeComplete}
          onBlur={handleBlur}
          onFocus={handleFocus}
          style={{ MozAppearance: 'textfield' }}
          disabled={disabled}
        />
        {arrows && !disabled && (
          <div className="pointer-events-none absolute top-1/2 right-1 flex -translate-y-1/2 flex-col">
            {/* Up arrow */}
            <button
              type="button"
              tabIndex={-1}
              className={cn(
                'pointer-events-auto cursor-pointer text-neutral-500 transition-all hover:text-neutral-700',
                isDragging && dragDirection === 'up' && 'text-blue-600',
              )}
              style={{ lineHeight: 0 }}
              onMouseDown={(e) => handleArrowMouseDown(e, 'up')} // Handle drag and single click
              data-qa-selector={dataQaSelector ? `${dataQaSelector}-up-arrow` : undefined}
            >
              <IconArrowUp className="h-3 w-3" />
            </button>
            {/* Down arrow */}
            <button
              type="button"
              tabIndex={-1}
              className={cn(
                'pointer-events-auto cursor-pointer text-neutral-500 transition-all hover:text-neutral-700',
                isDragging && dragDirection === 'down' && 'text-blue-600',
              )}
              style={{ lineHeight: 0 }}
              onMouseDown={(e) => handleArrowMouseDown(e, 'down')} // Handle drag and single click
              data-qa-selector={dataQaSelector ? `${dataQaSelector}-down-arrow` : undefined}
            >
              <IconArrowDown className="h-3 w-3" />
            </button>
          </div>
        )}
      </div>
    </div>
  );
};
